import { FC, useEffect, useRef } from "react";
import { useAIClimateAssistantStyles } from "./AIClimateAssistant.styles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import InputBox from "./InputBox";
import Header from "./Header";
import ChatWindow from "./ChatWindow";
import {
  addMessage,
  setDisplayPromptOverlay,
  setInitialLoad,
  setLoading,
  setPromptList,
  setQuery,
  setResetQuery,
  setSessionExpired,
  setShowMore,
  setTogglePromptOverlay,
  setTopicName,
  setVisibleCount,
  toggleAssistantWindow,
} from "../../store/slices/ClimateAssistantSlice";
import { useApi, useIsMobile, useSession } from "../../hooks";
import { getChatbotResponse, getPrompts } from "../../services";
import { generateMessage } from "./AIClimateAssistant.config";
import { formatDayTime } from "../../utils/dateFormatter";
import { AI_BASE_URL } from "../../lib/axios";
import MobilePromptDrawer from "./MobilePromptDrawer";

const AIClimateAssistant: FC = () => {
  const styles = useAIClimateAssistantStyles();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const { getSessionAndRequestId, stopSession } = useSession();

  const {
    loading,
    isExpanded,
    query,
    sessionExpired,
    topicName,
    openPromptDrawer,
  } = useSelector((state: RootState) => state.climateAssistant);

  const {
    data: chatbotResponse,
    execute,
    error,
    cancel,
  } = useApi(getChatbotResponse, {
    autoFetch: false,
  });

  const { data: promptList, execute: getPromptsList } = useApi(getPrompts, {
    args: [""],
  });

  useEffect(() => {
    if (promptList) {
      dispatch(setPromptList(promptList));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptList]);

  useEffect(() => {
    dispatch(setInitialLoad(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleScroll();
  }, [loading]);

  useEffect(() => {
    if (chatbotResponse) {
      const responseMessage = {
        requestId: chatbotResponse?.request_id,
        sessionId: "S123",
        sender: "bot",
        dateTime: formatDayTime(),
        sessionExpired,
        data: {
          text: chatbotResponse?.response?.text,
          graph: chatbotResponse?.response?.graph,
          table: chatbotResponse?.response?.table,
          graphSQLData: chatbotResponse?.response?.graphSQLData,
          tableSQLData: chatbotResponse?.response?.tableSQLData,
          csv: chatbotResponse?.response?.csv,
        },
      };

      dispatch(setLoading(false));
      dispatch(addMessage(responseMessage));

      if (chatbotResponse?.request_id === "req001") {
        dispatch(setTopicName(chatbotResponse?.topic ?? ""));
      }

      if (chatbotResponse?.request_id === "req003") {
        dispatch(setTopicName(""));
      }

      dispatch(setDisplayPromptOverlay(!sessionExpired));

      handleScroll();
    } else if (error) {
      dispatch(setLoading(false));

      const user = "bot";
      const text = "Something went wrong! please try again.";

      const options = {
        hasError: true,
      };

      const fallbackMsg = generateMessage(user, text, options);

      dispatch(addMessage(fallbackMsg));
      handleScroll();
    }

    //eslint-disable-next-line
  }, [chatbotResponse, error]);

  const handleSend = async (customQuery?: string, customTopic?: string) => {
    const q = customQuery ?? query;
    if (loading || !q.trim()) return;

    if (!isExpanded) {
      dispatch(toggleAssistantWindow(!isExpanded));
    }

    dispatch(setShowMore(false));
    dispatch(setVisibleCount(3));

    dispatch(setLoading(true));
    dispatch(setResetQuery(q));

    const user = "user";
    const userMessage = generateMessage(user, q);
    dispatch(addMessage(userMessage));

    const { sessionId, requestId, isNewSession } = getSessionAndRequestId();
    dispatch(setSessionExpired(isNewSession));

    if (requestId === "req001") {
      dispatch(setTopicName(customTopic ?? ""));
    }

    const payload = {
      userQuery: q,
      requestId,
      sessionId,
      topic:
        requestId === "req001"
          ? customTopic
            ? customTopic
            : topicName
          : topicName,
    };

    execute(payload);
    dispatch(setTogglePromptOverlay(false));
    dispatch(setQuery(""));
    handleScroll();
  };

  const handleScroll = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleStop = async () => {
    try {
      cancel();

      const { session } = stopSession();

      const payload = {
        previous_session_id: session.prevSessionId,
        new_session_id: session.sessionId,
        request_id: `req00${session.callCount + 1}`,
        steps_completed: session.callCount,
      };

      const response = await fetch(`${AI_BASE_URL}/stop`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "Ocp-Apim-Subscription-Key": process.env.REACT_APP_API_KEY ?? "",
        },
      });
      console.log(response);
    } catch (error) {
      console.log("Error calling stop API", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      <div
        className={
          isMobile
            ? isExpanded
              ? styles.mobileContainerExpanded
              : styles.mobileContainerCollapsed
            : styles.container
        }
      >
        <Header handleScroll={handleScroll} getPromptsList={getPromptsList} />
        {isExpanded && (
          <ChatWindow
            handleScroll={handleScroll}
            containerRef={containerRef}
            handleSend={handleSend}
          />
        )}

        {isExpanded && (
          <InputBox
            handleSend={handleSend}
            handleStop={handleStop}
            getPromptsList={getPromptsList}
          />
        )}

        {openPromptDrawer && <MobilePromptDrawer handleSend={handleSend} />}
      </div>
    </>
  );
};

export default AIClimateAssistant;
